import {
  closeWorkflowArtifactAtom,
  openArtifactAtom,
  setOpenArtifactAtom,
} from '@heidi/agents/data';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@heidi/design-system/breadcrumb';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { PanelToggleButton } from '@heidi/design-system/panel-toggle-button';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { useAtomValue, useSetAtom } from 'jotai';
import { Pencil } from 'lucide-react';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIsRoutinesOwnedOpenLastRunEnabled } from '@heidi/agents/flags';
import { useOpenRoutinesSurface } from '../hooks/useOpenRoutinesSurface';
import { useRecentRunsRail } from '../hooks/useRecentRunsRail';
import type { Workflow } from '../types';
import { RoutineActionsMenu } from './RoutineActionsMenu';
import { RoutineRecentRunsRail } from './RoutineRecentRunsRail';

/**
 * Chat header for a routine run on the standalone chat page.
 *
 * Left: a breadcrumb back to Routines and the routine's canonical name. Right:
 * a card/rail toggle and one overflow menu, grouped into routine
 * actions followed by run-level actions supplied by the host.
 *
 * Under {@link useIsRoutinesOwnedOpenLastRunEnabled}, the toggle opens/closes
 * the contextual routine card (the `{ type: 'workflow' }` artifact) from
 * ``sm`` up — the card replaces the recent-runs rail wherever the flag is on,
 * so there is one button and no popover. Flag off falls back to today's
 * Recent runs toggle, which has two presentations switched by CSS
 * breakpoints: from ``xl`` up it opens the docked rail beside the chat;
 * between ``sm`` and ``xl`` — where the rail has no room — the same button
 * opens the card in a popover anchored under the toggle, mirroring the scribe
 * control panel's compact behaviour. Below ``sm`` neither shows; the routine
 * configuration bottom sheet stays the path to run history on phones.
 *
 * Rename and Create routine stay excluded: a run's title comes from its routine,
 * and the chat already belongs to one. Save and Copy IDs remain available
 * because they act on the current chat.
 */
export function RoutineRunChatHeader({
  workflow,
  additionalMenuItems,
  headerActions,
  sidebarControl,
  compact = false,
  onOpenRoutines: onOpenRoutinesOverride,
}: {
  workflow: Workflow;
  additionalMenuItems?: ReactNode;
  headerActions?: ReactNode;
  sidebarControl?: ReactNode;
  compact?: boolean;
  /**
   * When set, a left-click on Routines calls this instead of navigating
   * via {@link useOpenRoutinesSurface}. Needed on the never-run landing so
   * the host can also close any open routine card.
   */
  onOpenRoutines?: () => void;
}): JSX.Element {
  const intl = useIntl();
  const workflowId = workflow.workflow_id;
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const closeWorkflowArtifact = useSetAtom(closeWorkflowArtifactAtom);
  const openArtifact = useAtomValue(openArtifactAtom);
  const isOwnedOpenLastRunEnabled = useIsRoutinesOwnedOpenLastRunEnabled();
  const isCardOpen =
    openArtifact?.type === 'workflow' && openArtifact.workflowId === workflowId;
  const openRoutines = useOpenRoutinesSurface();
  const {
    hasRuns,
    isOpen: isRailOpen,
    toggle: toggleRail,
  } = useRecentRunsRail(workflowId);
  // Below ``xl`` the docked rail has no room, so the same toggle opens the card
  // in a popover instead. Only reachable when the flag is off — the flag-on
  // toggle opens the card directly at every width from ``sm`` up.
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Close the popover when the viewport leaves the sm–xl band where its
  // trigger is visible. The slot is only CSS-hidden (``hidden sm:block
  // xl:hidden``), not unmounted, and Radix portals the content to <body> —
  // without this, a popover opened mid-band would stay stranded on screen
  // beside a display:none trigger after a resize. Bounds match the Tailwind
  // ``sm`` (640px) and ``xl`` (1280px) screens from @heidi/tailwind-config.
  useEffect(() => {
    if (!isPopoverOpen) return;
    const band = window.matchMedia('(min-width: 640px) and (max-width: 1279.98px)');
    const handleChange = (event: MediaQueryListEvent) => {
      if (!event.matches) setIsPopoverOpen(false);
    };
    band.addEventListener('change', handleChange);
    return () => band.removeEventListener('change', handleChange);
  }, [isPopoverOpen]);

  // Flag on: land the pencil directly in the card's Edit state (instructions
  // only, wired by the sibling task that reads `openInEdit`). Flag off keeps
  // today's behaviour of opening the plain overview.
  const openConfiguration = useCallback(() => {
    setOpenArtifact(
      isOwnedOpenLastRunEnabled
        ? { type: 'workflow', workflowId, openInEdit: true }
        : { type: 'workflow', workflowId }
    );
  }, [setOpenArtifact, workflowId, isOwnedOpenLastRunEnabled]);

  // Flag-on toggle: the routine card replaces the recent-runs rail, so this
  // just opens/closes the workflow artifact directly.
  const toggleCard = useCallback(() => {
    if (isCardOpen) {
      closeWorkflowArtifact();
      return;
    }
    setOpenArtifact({ type: 'workflow', workflowId });
  }, [isCardOpen, closeWorkflowArtifact, setOpenArtifact, workflowId]);

  const handleOpenRoutines = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      if (onOpenRoutinesOverride) {
        onOpenRoutinesOverride();
        return;
      }
      openRoutines(null);
    },
    [onOpenRoutinesOverride, openRoutines]
  );

  const hideRunsLabel = intl.formatMessage({
    defaultMessage: 'Hide recent runs',
    id: 'lw8uKaqMUj',
    description: 'Run chat header button: close the recent-runs card',
  });
  const showRunsLabel = intl.formatMessage({
    defaultMessage: 'Show recent runs',
    id: 'aLwZWESTtR',
    description: 'Run chat header button: open the recent-runs card',
  });
  const recentRunsLabel = isRailOpen ? hideRunsLabel : showRunsLabel;
  const popoverRunsLabel = isPopoverOpen ? hideRunsLabel : showRunsLabel;

  const hideRoutineLabel = intl.formatMessage({
    defaultMessage: 'Hide routine',
    id: 'umQUrCSd7r',
    description: 'Run chat header button: close the routine card',
  });
  const showRoutineLabel = intl.formatMessage({
    defaultMessage: 'Show routine',
    id: '8Ovolsm7hk',
    description: 'Run chat header button: open the routine card',
  });
  const routineCardLabel = isCardOpen ? hideRoutineLabel : showRoutineLabel;

  const editLabel = intl.formatMessage({
    defaultMessage: 'Edit',
    id: 'hLuTrLkd44',
    description: 'Routine overflow menu item: open this routine configuration',
  });
  const routinesLabel = intl.formatMessage({
    defaultMessage: 'Routines',
    id: 'YbZdceuF0B',
    description: 'Routine run chat breadcrumb link back to Routines',
  });

  return (
    <div
      className="flex min-w-0 flex-1 items-center justify-between gap-4"
      data-testid="routine-run-chat-header"
    >
      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="flex-nowrap gap-1.5 sm:gap-1.5">
          {sidebarControl ? (
            <BreadcrumbItem className="shrink-0">{sidebarControl}</BreadcrumbItem>
          ) : null}
          {!compact ? (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink href="/scribe/chat/projects" onClick={handleOpenRoutines}>
                  {routinesLabel}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </>
          ) : null}
          <BreadcrumbItem className="min-w-0">
            <BreadcrumbPage className="truncate">{workflow.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex shrink-0 items-center gap-1">
        {headerActions}
        <RoutineActionsMenu
          workflow={workflow}
          showDelete={false}
          showShare={false}
          routineMenuItems={
            <DropdownMenuItem onSelect={openConfiguration}>
              <Pencil aria-hidden />
              {editLabel}
            </DropdownMenuItem>
          }
          additionalMenuItems={additionalMenuItems}
        />
        {isOwnedOpenLastRunEnabled ? (
          <div className="hidden sm:block" data-testid="routine-card-toggle-slot">
            <Tooltip>
              <TooltipTrigger asChild>
                <PanelToggleButton
                  type="button"
                  open={isCardOpen}
                  side="right"
                  variant="ghost"
                  size="sm"
                  className="text-text-primary"
                  aria-label={routineCardLabel}
                  aria-pressed={isCardOpen}
                  onClick={toggleCard}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">{routineCardLabel}</TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <>
            {hasRuns ? (
              <div className="hidden xl:block" data-testid="recent-runs-rail-toggle-slot">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PanelToggleButton
                      type="button"
                      open={isRailOpen}
                      side="right"
                      variant="ghost"
                      size="sm"
                      className="text-text-primary"
                      aria-label={recentRunsLabel}
                      onClick={toggleRail}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{recentRunsLabel}</TooltipContent>
                </Tooltip>
              </div>
            ) : null}
            {hasRuns ? (
              <div
                className="hidden sm:block xl:hidden"
                data-testid="recent-runs-popover-slot"
              >
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <PanelToggleButton
                          type="button"
                          open={isPopoverOpen}
                          side="right"
                          variant="ghost"
                          size="sm"
                          className="text-text-primary"
                          aria-label={popoverRunsLabel}
                        />
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">{popoverRunsLabel}</TooltipContent>
                  </Tooltip>
                  {/* The popover supplies the overlay chrome (surface-elevated +
                      shadow-popover, which diverge from the card's inline chrome
                      in dark mode); the card drops its own in standalone mode. */}
                  <PopoverContent side="bottom" align="end" className="w-auto p-0">
                    <RoutineRecentRunsRail
                      workflowId={workflowId}
                      standalone
                      onRunOpen={() => setIsPopoverOpen(false)}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
